import { QueryClient } from "@tanstack/react-query";
import { ContatoWA, WhatsAppMsg } from "./types";
import { invalidateContatosWA } from "./useWhatsAppContatos";
import { markWhatsAppContactRead } from "./useWhatsAppConversas";

type WhatsAppConnectionStatus = "conectado" | "desconectado";

function parsePayload(value: any): any {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function firstDefined<T>(...values: T[]): T | undefined {
  return values.find((value) => value !== undefined && value !== null);
}

function asObject(value: any): Record<string, any> {
  const parsed = parsePayload(value);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed
    : {};
}

function getMessagePayload(payload: any): Record<string, any> {
  const parsedPayload = asObject(payload);
  const data = asObject(parsedPayload?.data);
  const message = asObject(parsedPayload?.message);
  const mensagem = asObject(parsedPayload?.mensagem);

  if (Array.isArray(data?.messages) && data.messages.length > 0) {
    return asObject(data.messages[0]);
  }

  if (
    Array.isArray(parsedPayload?.messages) &&
    parsedPayload.messages.length > 0
  ) {
    return asObject(parsedPayload.messages[0]);
  }

  if (Array.isArray(message?.messages) && message.messages.length > 0) {
    return asObject(message.messages[0]);
  }

  if (Array.isArray(mensagem?.messages) && mensagem.messages.length > 0) {
    return asObject(mensagem.messages[0]);
  }

  if (Array.isArray(data?.data) && data.data.length > 0) {
    return asObject(data.data[0]);
  }

  if (Array.isArray(parsedPayload?.data) && parsedPayload.data.length > 0) {
    return asObject(parsedPayload.data[0]);
  }

  if (Object.keys(data).length > 0) return data;
  if (Object.keys(message).length > 0) return message;
  if (Object.keys(mensagem).length > 0) return mensagem;

  return parsedPayload;
}

function getPayloadMeta(payload: any): Record<string, any> {
  const parsedPayload = asObject(payload);
  const data = asObject(parsedPayload?.data);

  return {
    ...parsedPayload,
    ...(Object.keys(data).length > 0 ? data : {}),
  };
}

function normalizeEventType(payload: any): string {
  const parsedPayload = asObject(payload);
  return String(
    firstDefined(
      parsedPayload?.tipo,
      parsedPayload?.type,
      parsedPayload?.event,
      parsedPayload?.eventType,
      parsedPayload?.name,
      parsedPayload?.data?.tipo,
      parsedPayload?.data?.type,
      parsedPayload?.data?.event
    ) || ""
  )
    .trim()
    .toLowerCase();
}

function normalizeHubDate(rawValue: any): string {
  const value = firstDefined(rawValue, new Date().toISOString());

  if (typeof value === "number") {
    const timestamp = value < 1000000000000 ? value * 1000 : value;
    return new Date(timestamp).toISOString();
  }

  const text = String(value || new Date().toISOString()).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(text)) {
    return text.replace(/Z$/, "");
  }

  return text;
}

function extractMessageText(detail: any, payload: any): string {
  const nestedMessage = asObject(detail?.message);

  return String(
    firstDefined(
      detail?.mensagem,
      detail?.texto,
      detail?.body,
      detail?.content,
      detail?.caption,
      detail?.text,
      nestedMessage?.conversation,
      nestedMessage?.extendedTextMessage?.text,
      nestedMessage?.imageMessage?.caption,
      nestedMessage?.videoMessage?.caption,
      payload?.mensagem,
      payload?.texto,
      payload?.body,
      payload?.content,
      ""
    ) || ""
  );
}

function extractPhone(detail: any, payload: any): string {
  const key = asObject(detail?.key);
  const remoteJid = String(
    firstDefined(key?.remoteJid, detail?.remoteJid) || ""
  );

  return String(
    firstDefined(
      detail?.telefone,
      detail?.phone,
      detail?.numero,
      detail?.from,
      payload?.telefone,
      payload?.phone,
      remoteJid.split("@")[0]
    ) || ""
  ).replace(/\D/g, "");
}

function isSamePhone(a?: string, b?: string): boolean {
  const phoneA = String(a || "").replace(/\D/g, "");
  const phoneB = String(b || "").replace(/\D/g, "");

  if (!phoneA || !phoneB) return false;
  return phoneA.endsWith(phoneB) || phoneB.endsWith(phoneA);
}

function resolveMatchedContact(
  contatos: ContatoWA[],
  leadId: string | undefined,
  phone: string
): ContatoWA | undefined {
  return contatos.find((c) => {
    const matchesLead =
      !!leadId &&
      (c.leadCorretorId === leadId || c.rowKey === leadId || c.id === leadId);
    return matchesLead || isSamePhone(c.telefone, phone);
  });
}

function resolveStatus(payload: any): WhatsAppConnectionStatus | undefined {
  if (typeof payload === "boolean") {
    return payload ? "conectado" : "desconectado";
  }

  if (typeof payload === "string") {
    const text = payload.trim().toLowerCase();
    if (text === "true" || text === "connected" || text === "conectado") {
      return "conectado";
    }
    if (
      text === "false" ||
      text === "disconnected" ||
      text === "desconectado"
    ) {
      return "desconectado";
    }
  }

  const parsedPayload = asObject(payload);
  const data = asObject(parsedPayload?.data);
  const status = String(
    firstDefined(
      parsedPayload?.status,
      data?.status,
      parsedPayload?.state,
      data?.state,
      parsedPayload?.connectionStatus,
      data?.connectionStatus,
      parsedPayload?.estado,
      data?.estado
    ) || ""
  )
    .trim()
    .toLowerCase();

  if (status === "conectado" || status === "connected") return "conectado";
  if (status === "desconectado" || status === "disconnected")
    return "desconectado";

  return undefined;
}

function updateConnectionStatus(
  _queryClient: QueryClient,
  payload: any,
  onConnectionStatus?: (connected: boolean) => void
) {
  const status = resolveStatus(payload);
  if (!status) return false;

  const conectado = status === "conectado";
  // IMPORTANTE: NÃO sobrescrevemos o cache de ["whatsapp-status"] aqui.
  // O hub emite eventos de connection.update muito frequentes (estado do
  // device, ping, etc.) e em alguns casos relata "desconectado" mesmo com a
  // sessão do servidor ativa. Isso causava o `isWhatsConnected` oscilar e
  // disparar reconexões em loop. A fonte de verdade é o polling de /status/v1
  // (useWhatsAppStatus, refetchInterval=30s). Aqui só notificamos consumidores
  // que quiserem reagir pontualmente (UI/telemetria) via callback.
  onConnectionStatus?.(conectado);

  return true;
}

export function handleWhatsAppHubPayload({
  queryClient,
  payload,
  activeLeadId,
  onConnectionStatus,
}: {
  queryClient: QueryClient;
  payload: any;
  activeLeadId?: string;
  onConnectionStatus?: (connected: boolean) => void;
}) {
  const parsedPayload = parsePayload(payload);
  const eventType = normalizeEventType(parsedPayload);
  if (eventType === "connection.update" || eventType === "status") {
    updateConnectionStatus(queryClient, parsedPayload, onConnectionStatus);
    return;
  }

  const detail = getMessagePayload(parsedPayload);
  const meta = getPayloadMeta(parsedPayload);
  const key = asObject(detail?.key);
  const rawLeadId = firstDefined(
    meta?.leadId,
    meta?.idLeadCorretor365,
    meta?.idLeadCorretor,
    meta?.leadCorretorId,
    detail?.leadId,
    detail?.idLeadCorretor365,
    detail?.idLeadCorretor,
    detail?.leadCorretorId,
    detail?.rowKey,
    detail?.idContato
  );
  const leadId = rawLeadId ? String(rawLeadId) : undefined;
  const phone = extractPhone(detail, meta);
  const contatos = queryClient.getQueryData<ContatoWA[]>(["contatos-wa"]) ?? [];
  const matched = resolveMatchedContact(contatos, leadId, phone);
  const convKeyId =
    matched?.leadCorretorId ||
    matched?.rowKey ||
    matched?.id ||
    leadId ||
    activeLeadId;

  if (!convKeyId) {
    // Não temos como identificar a conversa. Em vez de fazer refetch imediato
    // (que entra em loop quando o backend manda muitos eventos sem leadId),
    // só agenda um invalidate se a query NÃO estiver fetching e se já passou
    // tempo suficiente desde o último invalidate.
    invalidateContatosWA(queryClient);
    return;
  }

  const mensagem = extractMessageText(detail, meta);
  const mediaType = firstDefined(
    detail?.mediaType,
    detail?.mimeType,
    detail?.messageType,
    meta?.mediaType
  );
  const mediaUrl = firstDefined(
    detail?.mediaUrl,
    detail?.url,
    detail?.fileUrl,
    meta?.mediaUrl
  );
  const data = normalizeHubDate(
    firstDefined(
      detail?.data,
      detail?.dataCriacao,
      detail?.timestamp,
      detail?.messageTimestamp,
      meta?.dataCriacao,
      meta?.timestamp,
      meta?.messageTimestamp,
      meta?.UltimaAtividade,
      meta?.ultimaAtividade
    )
  );
  const rawDirecao = firstDefined(
    detail?.Direcao,
    detail?.direcao,
    meta?.Direcao,
    meta?.direcao
  );
  const fromMe = Boolean(
    firstDefined(
      detail?.fromMe,
      key?.fromMe,
      meta?.fromMe,
      meta?.ultimaMensagemFromMe,
      false
    )
  );
  let direcao: "saida" | "entrada";
  if (rawDirecao === "saida" || rawDirecao === "entrada") {
    direcao = rawDirecao;
  } else {
    direcao = fromMe ? "saida" : "entrada";
  }
  const unreadCount = firstDefined(
    detail?.UnreadCount,
    detail?.unreadCount,
    meta?.UnreadCount,
    meta?.unreadCount
  );
  const ultimaAtividade = normalizeHubDate(
    firstDefined(
      detail?.ultimaAtividade,
      detail?.UltimaAtividade,
      meta?.ultimaAtividade,
      meta?.UltimaAtividade,
      data
    )
  );
  const isActiveConversation =
    !!activeLeadId && (activeLeadId === convKeyId || activeLeadId === leadId);

  if (isActiveConversation) {
    markWhatsAppContactRead(queryClient, convKeyId, ultimaAtividade);
  }

  queryClient.setQueryData<WhatsAppMsg[]>(["wa-conv", convKeyId], (old) => {
    const list = old ?? [];
    const msgId = String(
      firstDefined(
        detail?.id,
        detail?.messageId,
        detail?.rowKey,
        key?.id,
        meta?.id,
        meta?.rowKey,
        `signalr-${convKeyId}-${data}-${mensagem.slice(0, 16)}`
      )
    );

    if (list.some((m) => m.id === msgId)) return list;

    return [
      ...list,
      {
        id: msgId,
        leadId: convKeyId,
        mensagem,
        direcao,
        data,
        mediaUrl: mediaUrl ? String(mediaUrl) : undefined,
        mediaType: mediaType ? String(mediaType) : undefined,
      },
    ];
  });

  queryClient.setQueryData<ContatoWA[]>(["contatos-wa"], (old) => {
    if (!old) return old;

    const viewedAt = localStorage.getItem(`wa_conv_viewed_${convKeyId}`);
    const viewedAfter =
      isActiveConversation ||
      (viewedAt && new Date(ultimaAtividade) <= new Date(viewedAt));

    return old.map((c) => {
      const isCurrent =
        c.leadCorretorId === convKeyId ||
        c.rowKey === convKeyId ||
        c.id === convKeyId ||
        c.leadCorretorId === leadId ||
        c.rowKey === leadId ||
        c.id === leadId ||
        isSamePhone(c.telefone, phone);

      if (!isCurrent) return c;

      const nextUnreadCount =
        unreadCount ??
        (direcao === "saida" ? c.unreadCount : (c.unreadCount || 0) + 1);

      return {
        ...c,
        ultimaMensagem: mensagem || c.ultimaMensagem,
        ultimaMensagemFromMe: direcao === "saida",
        ultimaAtividade,
        unreadCount: viewedAfter ? 0 : nextUnreadCount,
      };
    });
  });
}
